import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export interface User {
  id: number
  email: string
  name?: string
  country_code?: string
  phone?: string
  is_bangkok_resident?: boolean
  whatsapp_number?: string
  social_links?: {
    line?: string
    instagram?: string
    tiktok?: string
    facebook?: string
  }
  role: string
}

export const useUserStore = defineStore('user', () => {
  // State
  const user = ref<User | null>(null)
  const isLoading = ref(false)
  const isAuthenticated = ref(false)

  // Getters
  const isLoggedIn = computed(() => isAuthenticated.value && user.value !== null)
  
  const displayName = computed(() => {
    return user.value?.name || user.value?.email || 'Guest'
  })

  const contactInfo = computed(() => {
    if (!user.value) return null
    return {
      email: user.value.email || '',
      fullName: user.value.name || '',
      country: (user.value as any).country || '',
      countryCode: user.value.country_code || '+66',
      phoneNumber: user.value.phone || '',
      isBangkokResident: user.value.is_bangkok_resident || false,
      whatsappNumber: user.value.whatsapp_number || '',
      line: user.value.social_links?.line || '',
      instagram: user.value.social_links?.instagram || '',
      tiktok: user.value.social_links?.tiktok || '',
      facebook: user.value.social_links?.facebook || ''
    }
  })

  // Actions
  async function fetchCurrentUser(): Promise<void> {
    isLoading.value = true
    try {
      const response = await fetch('/api/v1/user', {
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        user.value = data.user
        isAuthenticated.value = true
      } else if (response.status === 401) {
        user.value = null
        isAuthenticated.value = false
      }
    } catch (error) {
      console.error('Failed to fetch user:', error)
      user.value = null
      isAuthenticated.value = false
    } finally {
      isLoading.value = false
    }
  }

  function setUser(userData: User | null) {
    user.value = userData
    isAuthenticated.value = userData !== null
  }

  function logout() {
    user.value = null
    isAuthenticated.value = false
  }

  return {
    // State
    user,
    isLoading,
    isAuthenticated,
    
    // Getters
    isLoggedIn,
    displayName,
    contactInfo,
    
    // Actions
    fetchCurrentUser,
    setUser,
    logout
  }
})
